import { Injectable } from '@angular/core';
import { OfflineEntityRow, offlineDb } from './offline-db';

/** Default TTL for availability slots cache (v1: block book if stale). */
export const SLOTS_TTL_MS = 20 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class OfflineCacheService {
  async put(key: string, data: unknown, ttlMs?: number, serverSkewMs?: number): Promise<void> {
    const row: OfflineEntityRow = {
      key,
      data,
      updatedAt: Date.now(),
      ttlMs,
      serverSkewMs,
    };
    await offlineDb.entities.put(row);
  }

  async get<T = unknown>(key: string): Promise<T | null> {
    const row = await offlineDb.entities.get(key);
    if (!row) return null;
    return row.data as T;
  }

  async invalidate(key: string): Promise<void> {
    await offlineDb.entities.delete(key);
  }

  async getRow(key: string): Promise<OfflineEntityRow | undefined> {
    return offlineDb.entities.get(key);
  }

  /** Returns data only if present and not past TTL (when ttlMs set). */
  async getFresh<T = unknown>(key: string): Promise<{ data: T; row: OfflineEntityRow } | null> {
    const row = await offlineDb.entities.get(key);
    if (!row) return null;
    if (row.ttlMs != null && Date.now() - row.updatedAt > row.ttlMs) {
      return null;
    }
    return { data: row.data as T, row };
  }

  isStale(row: OfflineEntityRow | undefined | null, ttlMs = SLOTS_TTL_MS): boolean {
    if (!row) return true;
    const limit = row.ttlMs ?? ttlMs;
    return Date.now() - row.updatedAt > limit;
  }

  /** Skew-adjusted "now" using last cached serverSkewMs if any. */
  adjustedNow(row?: OfflineEntityRow | null): number {
    const skew = row?.serverSkewMs ?? 0;
    return Date.now() + skew;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await offlineDb.meta.put({ key, value });
  }

  async getMeta<T = unknown>(key: string): Promise<T | null> {
    const row = await offlineDb.meta.get(key);
    return row ? (row.value as T) : null;
  }

  async setServerSkewFromDateHeader(dateHeader: string | null): Promise<void> {
    if (!dateHeader) return;
    const serverMs = Date.parse(dateHeader);
    if (Number.isNaN(serverMs)) return;
    const skew = serverMs - Date.now();
    await this.setMeta('serverSkewMs', skew);
  }

  async getServerSkewMs(): Promise<number> {
    return (await this.getMeta<number>('serverSkewMs')) ?? 0;
  }
}

/** Cache key helpers for kanban / journey. */
export const OfflineKeys = {
  kanbanTasks: (domain: string) => `kanban:tasks:${domain}`,
  kanbanArchives: (domain: string) => `kanban:archives:${domain}`,
  kanbanTrashes: (domain: string) => `kanban:trashes:${domain}`,
  kanbanTeams: (domain: string) => `kanban:teams:${domain}`,
  journeyMonth: (domain: string, ym: string) => `journey:month:${domain}:${ym}`,
  appointments: (domain: string, role: string) => `journey:appointments:${domain}:${role}`,
  slots: (domain: string, day: string, practitionerId = '') =>
    `journey:slots:${domain}:${day}:${practitionerId}`,
  mapViewport: (domain: string) => `map:viewport:${domain}`,
};
